import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "i18n"

// Material UI
import { makeStyles } from "@material-ui/core/styles"
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  useTheme,
  useMediaQuery
} from "@material-ui/core"

// Icons
import TodayIcon from "@material-ui/icons/Today"
import TimelineIcon from "@material-ui/icons/Timeline"
import PublicIcon from "@material-ui/icons/Public"
import LanguageIcon from "@material-ui/icons/Language"
import TranslateIcon from "@material-ui/icons/Translate"

// Helpers
import { getPluralOrSingular } from "shared/helpers"

const useStyles = makeStyles(theme => ({
  avatarRoot: {
    backgroundColor: "transparent"
  },
  icon: {
    fontSize: 40,
    color: theme.palette.darkBlue
  },
  primaryText: {
    textTransform: "capitalize",
    fontWeight: theme.typography.fontWeightMedium
  },
  listRoot: {
    padding: theme.spacing(0)
  }
}))

export default function KeyHighlightsList({
  type,
  prices: products,
  duration,
  available_countries,
  language,
  level
}) {
  const { t } = useTranslation()

  const classes = useStyles()
  const theme = useTheme()

  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  return (
    <List
      classes={{
        root: classes.listRoot
      }}
    >
      {products && (
        <ListItem disableGutters={smDown}>
          <ListItemAvatar>
            <Avatar
              classes={{
                root: classes.avatarRoot
              }}
            >
              <LanguageIcon className={classes.icon} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="h6" className={classes.primaryText}>
                {`Learning Mode${products.length > 1 ? "s" : ""}`}
              </Typography>
            }
            secondary={
              <Typography variant="body1" color="textSecondary">
                {products.map(product => product.learning_mode).join(", ")}
              </Typography>
            }
          />
        </ListItem>
      )}
      {duration !== undefined && (
        <ListItem disableGutters={smDown}>
          <ListItemAvatar>
            <Avatar
              classes={{
                root: classes.avatarRoot
              }}
            >
              <TodayIcon className={classes.icon} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="h6" className={classes.primaryText}>
                Estimated Duration
              </Typography>
            }
            secondary={
              <Typography variant="body1" color="textSecondary">
                {getPluralOrSingular("day", duration)}
              </Typography>
            }
          />
        </ListItem>
      )}

      <ListItem disableGutters={smDown}>
        <ListItemAvatar>
          <Avatar
            classes={{
              root: classes.avatarRoot
            }}
          >
            <PublicIcon className={classes.icon} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="h6" className={classes.primaryText}>
              {available_countries.globally_available
                ? t("Global Reach")
                : t("Reach")}
            </Typography>
          }
          secondary={
            <Typography variant="body1" color="textSecondary">
              This {type} is available in&nbsp;
              {available_countries.globally_available
                ? "all countries"
                : available_countries.available_countries.join(", ")}
            </Typography>
          }
        />
      </ListItem>
      <ListItem disableGutters={smDown}>
        <ListItemAvatar>
          <Avatar
            classes={{
              root: classes.avatarRoot
            }}
          >
            <TranslateIcon className={classes.icon} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="h6" className={classes.primaryText}>
              Languages
            </Typography>
          }
          // TBD
          secondary={
            <Typography variant="body1" color="textSecondary">
              {language.join(", ")}
            </Typography>
          }
        />
      </ListItem>
      {level !== undefined && (
        <ListItem disableGutters={smDown}>
          <ListItemAvatar>
            <Avatar
              classes={{
                root: classes.avatarRoot
              }}
            >
              <TimelineIcon className={classes.icon} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="h6" className={classes.primaryText}>
                {level}
              </Typography>
            }
            secondary={
              <Typography variant="body1" color="textSecondary">
                This {type} is aimed towards {level} learners
              </Typography>
            }
          />
        </ListItem>
      )}
    </List>
  )
}

KeyHighlightsList.propTypes = {
  type: PropTypes.oneOf([
    "course",
    "certification",
    "qualification",
    "package"
  ]),
  prices: PropTypes.array,
  duration: PropTypes.number,
  available_countries: PropTypes.object,
  language: PropTypes.array,
  level: PropTypes.string
}

KeyHighlightsList.defaultProps = {
  type: "course"
}
